/**
 * Regalo automático en el carrito (assets/cart-gift.js).
 *
 * Lee la configuración de los data- de <cart-gift> (snippets/cart-gift.liquid),
 * que salen de Configuración del tema → Regalo en carrito.
 *
 * - Añade el regalo por defecto cuando el carrito (sin contar los regalos) llega al mínimo.
 * - Lo quita si el carrito baja del mínimo o la campaña está fuera de fechas.
 * - Si el cliente lo quita a mano, no se lo vuelve a meter en esa sesión.
 * - Deja cambiar el regalo (color) con los botones del snippet.
 * - Seguridad: si el regalo no sale a 0 € (descuento apagado), lo quita.
 *
 * El 0 € lo pone la app de descuentos; este archivo solo gestiona la línea del carrito.
 */
(() => {
  if (window.CartGift) return;

  const PROP = '_cart_gift';
  const KEYS = {
    had: 'cartGift:had',
    declined: 'cartGift:declined',
    failedAt: 'cartGift:failedAt'
  };

  const store = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        if (value === null) {
          window.sessionStorage.removeItem(key);
        } else {
          window.sessionStorage.setItem(key, value);
        }
      } catch (e) {
        /* sin sessionStorage: el regalo funciona igual, solo no recuerda si se rechazó */
      }
    }
  };

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const toIds = (value) =>
    String(value || '')
      .split(',')
      .map((id) => Number(id))
      .filter(Boolean);

  const isDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);

  const todayIn = (timeZone) => {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date());
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  };

  const readConfig = () => {
    const el = document.querySelector('cart-gift');
    if (!el) return null;
    const d = el.dataset;
    return {
      enabled: d.enabled === 'true',
      start: (d.start || '').trim(),
      end: (d.end || '').trim(),
      timeZone: d.timezone || 'Europe/Madrid',
      threshold: parseInt(d.threshold, 10) || 0,
      productIds: toIds(d.productIds),
      variantIds: toIds(d.availableVariantIds),
      defaultVariantId: Number(d.defaultVariantId) || 0,
      checkPrice: d.checkPrice !== 'false',
      hasGiftLine: d.hasGiftLine === 'true'
    };
  };

  const isActive = (config) => {
    if (!config.enabled || config.threshold <= 0 || !config.productIds.length) return false;
    const today = todayIn(config.timeZone);
    if (config.start && (!isDate(config.start) || today < config.start)) return false;
    if (config.end && (!isDate(config.end) || today > config.end)) return false;
    return true;
  };

  const isGiftLine = (item) => Boolean(item.properties && item.properties[PROP]);

  const giftLinesOf = (cart) => cart.items.filter(isGiftLine);

  const eligibleOf = (cart, config) =>
    cart.items.reduce((sum, item) => {
      if (isGiftLine(item) || config.productIds.includes(item.product_id)) return sum;
      return sum + item.final_line_price;
    }, 0);

  const pickVariant = (config) => {
    if (config.variantIds.includes(config.defaultVariantId)) return config.defaultVariantId;
    return config.variantIds[0] || 0;
  };

  const getCart = async () => {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  };

  const post = async (path, body) => {
    const response = await fetch(`${root()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const json = await response.json();
    if (!response.ok || json.status) {
      throw new Error(json.description || json.message || `Error en ${path}`);
    }
    return json;
  };

  const removeLine = (line) => post('cart/change.js', { id: line.key, quantity: 0 });

  const addGift = (variantId) =>
    post('cart/add.js', { items: [{ id: variantId, quantity: 1, properties: { [PROP]: '1' } }] });

  /**
   * Comprueba que el regalo sale a 0 €. Si no (descuento apagado o la app no
   * lo da por válido), lo quita y no lo reintenta hasta que cambie el carrito.
   */
    const isFree = (cart, line) => {
    if (line.final_line_price <= 1) return true;
    const orderDiscount = (cart.cart_level_discount_applications || []).reduce(
      (sum, discount) => sum + (discount.total_allocated_amount || 0),
      0
    );
    return orderDiscount >= line.final_line_price - 1;
  };
  const verifyPrice = async (config) => {
    const cart = await getCart();
    const line = giftLinesOf(cart)[0];
    if (!line) return;

    if (config.checkPrice && !isFree(cart, line)) {
      await removeLine(line);
      store.set(KEYS.failedAt, String(eligibleOf(cart, config)));
      store.set(KEYS.had, null);
      console.warn('[cart-gift] El regalo no sale a 0 €: revisa que el descuento de la app esté activo.');
      return;
    }

    store.set(KEYS.had, '1');
  };

  /**
   * Pide al tema que repinte el drawer o la página de carrito, con el mismo
   * mecanismo que usa cart.js (cart:grouped-sections + pubsub cartUpdate).
   */
  const publish = async () => {
    const ids = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: ids } })
    );
    const sectionIds = [...new Set(ids)].filter(Boolean);

    let sections = null;
    if (sectionIds.length) {
      try {
        const response = await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`);
        sections = await response.json();
        if (!sectionIds.every((id) => sections && sections[id])) sections = null;
      } catch (e) {
        sections = null;
      }
    }

    const hasPubSub = window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS;

    if (!sections || !hasPubSub) {
      if (document.querySelector('main-cart')) {
        window.location.reload();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
      }
      return;
    }

    const cart = await getCart();
    cart.sections = sections;
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
  };

  let busy = false;
  let pending = false;

  const run = async (task) => {
    if (busy) {
      pending = true;
      return;
    }
    busy = true;
    try {
      await task();
    } catch (error) {
      console.warn('[cart-gift]', error);
    } finally {
      busy = false;
      if (pending) {
        pending = false;
        schedule();
      }
    }
  };

  const sync = () =>
    run(async () => {
      const config = readConfig();
      if (!config) return;

      const active = isActive(config);
      // Fuera de campaña y sin regalo en el carrito: nada que hacer, ni siquiera pedir el carrito.
      if (!active && !config.hasGiftLine && store.get(KEYS.had) !== '1') return;

      const cart = await getCart();
      const lines = giftLinesOf(cart);
      const eligible = eligibleOf(cart, config);
      const qualifies =
        active && eligible >= config.threshold && store.get(KEYS.failedAt) !== String(eligible);

      let changed = false;

      if (!qualifies) {
        for (const line of lines) {
          await removeLine(line);
          changed = true;
        }
        store.set(KEYS.had, null);
      } else if (!lines.length) {
        if (store.get(KEYS.had) === '1') {
          // Lo ha quitado el cliente: respetarlo durante esta sesión.
          store.set(KEYS.declined, '1');
          store.set(KEYS.had, null);
        } else if (store.get(KEYS.declined) !== '1') {
          const variantId = pickVariant(config);
          if (variantId) {
            await addGift(variantId);
            await verifyPrice(config);
            changed = true;
          }
        }
      } else {
        // Solo un regalo y con cantidad 1.
        for (const extra of lines.slice(1)) {
          await removeLine(extra);
          changed = true;
        }
        if (lines[0].quantity > 1) {
          await post('cart/change.js', { id: lines[0].key, quantity: 1 });
          changed = true;
        }
        if (config.checkPrice && !isFree(cart, lines[0])) {
          await verifyPrice(config);
          changed = true;
        } else {
          store.set(KEYS.had, '1');
        }
      }

      if (changed) await publish();
    });

  const choose = (variantId, button) =>
    run(async () => {
      const config = readConfig();
      if (!config || !variantId) return;

      const cart = await getCart();
      const lines = giftLinesOf(cart);
      if (lines.length === 1 && lines[0].variant_id === variantId) return;

      if (button) button.setAttribute('aria-busy', 'true');
      store.set(KEYS.declined, null);
      store.set(KEYS.had, null);

      try {
        for (const line of lines) {
          await removeLine(line);
        }
        await addGift(variantId);
        await verifyPrice(config);
        await publish();
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    });

  let timer;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, 200);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-gift-variant]');
    if (!button) return;
    event.preventDefault();
    choose(Number(button.dataset.cartGiftVariant), button);
  });

  const init = () => {
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, schedule);
    }
    document.addEventListener('cart:updated', schedule);
    document.addEventListener('product-ajax:added', schedule);
    schedule();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CartGift = { sync: schedule };
})();